import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { getAladdinExportOptions, whoami } from "../services/APIClient";

const emptyState = {
  globalEnabled: false,
  navigationAvailable: false,
  options: [],
  nextUpcoming: null,
};

const AladdinExportAvailabilityContext = createContext({
  ...emptyState,
  loading: false,
  error: null,
  schoolId: null,
  refresh: () => Promise.resolve(),
});

export const formatAladdinAvailabilityDate = (value) => {
  if (!value) return "";
  return new Intl.DateTimeFormat("en-IE", {
    timeZone: "Europe/Dublin",
    dateStyle: "long",
    timeStyle: "short",
  }).format(new Date(value));
};

export function AladdinExportAvailabilityProvider({ children }) {
  const location = useLocation();
  const [state, setState] = useState(emptyState);
  const [schoolId, setSchoolId] = useState(null);
  const [loadedToken, setLoadedToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoadedToken(null);
      setSchoolId(null);
      setState(emptyState);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const identity = await whoami();
      const resolvedSchoolId = identity.data.stakeholder.schoolid;
      const options = await getAladdinExportOptions(resolvedSchoolId);
      setSchoolId(resolvedSchoolId);
      setState({ ...emptyState, ...options });
      setLoadedToken(token);
    } catch (loadError) {
      console.error("Failed to resolve Aladdin Export availability:", loadError);
      setState(emptyState);
      setError("Aladdin Export availability could not be loaded.");
      setLoadedToken(token);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token !== loadedToken) load();
  }, [load, loadedToken, location.pathname]);

  const value = useMemo(
    () => ({ ...state, schoolId, loading, error, refresh: load }),
    [state, schoolId, loading, error, load]
  );

  return (
    <AladdinExportAvailabilityContext.Provider value={value}>
      {children}
    </AladdinExportAvailabilityContext.Provider>
  );
}

export function useAladdinExportAvailability() {
  return useContext(AladdinExportAvailabilityContext);
}

export function AladdinExportAvailabilityGate({ children }) {
  const { navigationAvailable, options, nextUpcoming, loading, error } =
    useAladdinExportAvailability();
  if (loading) {
    return <div className="spinner-container"><div className="big-ball-spinner" /></div>;
  }
  if (navigationAvailable && options.some((option) => option.available)) return children;
  const notice = nextUpcoming || options.find((option) => !option.available);
  return (
    <Card className="m-4">
      <Card.Body className="text-center py-5">
        <h3>Aladdin Export is currently unavailable</h3>
        <p className="lead">{notice?.message || error || "No Aladdin exports are currently available."}</p>
        {notice?.availableFrom && (
          <Alert variant="info">
            Scheduled availability: {formatAladdinAvailabilityDate(notice.availableFrom)}
          </Alert>
        )}
        <Button href="/masterdashboard">Return to Home</Button>
      </Card.Body>
    </Card>
  );
}
